<template>
    <slot />
</template>

<script>
import { normalizeDockZone, DOCK_ZONES_LIST } from './constants.js';
import {
    mergeRootZonePolicies,
    normalizeRootZonePolicy,
} from './dockPolicyEngine.js';

/**
 * Declares a layout region block (split or tabs) for default blueprint assembly.
 */
export default {
    name: 'MultipanelDockZone',

    props: {
        zone: {
            type: String,
            default: null,
            validator: (v) => {
                if (v == null || v === '') return true;
                return DOCK_ZONES_LIST.includes(String(v).toLowerCase());
            },
        },
        layout: {
            type: String,
            default: 'tabs',
            validator: (v) => ['split', 'tabs'].includes(v),
        },
        orientation: {
            type: String,
            default: 'horizontal',
            validator: (v) => ['horizontal', 'vertical'].includes(v),
        },
        sizes: { type: Array, default: null },
        allowedZones: { type: [String, Array], default: null },
        disallowedZones: { type: [String, Array], default: null },
    },

    inject: {
        multipanelDockLayout: { default: null },
        multipanelDockInheritedZone: { default: null },
        multipanelDockInheritedRestrictions: { default: null },
    },

    data() {
        return {
            _blockKey: `zone-${Math.random().toString(36).slice(2)}`,
        };
    },

    provide() {
        const parentZone = this.multipanelDockInheritedZone;
        const resolved = this.zone != null && this.zone !== ''
            ? normalizeDockZone(this.zone)
            : parentZone;
        const inheritedPolicy = normalizeRootZonePolicy(
            this.multipanelDockInheritedRestrictions || {},
        );
        const ownPolicy = normalizeRootZonePolicy({
            allowedZones: this.allowedZones,
            disallowedZones: this.disallowedZones,
        });
        const mergedPolicy = mergeRootZonePolicies(inheritedPolicy, ownPolicy);
        return {
            multipanelDockInheritedZone: resolved,
            multipanelDockZoneBlockKey: this._blockKey,
            multipanelDockInheritedRestrictions: mergedPolicy,
        };
    },

    computed: {
        resolvedZone() {
            const parent = this.multipanelDockInheritedZone;
            if (this.zone != null && this.zone !== '') {
                return normalizeDockZone(this.zone);
            }
            return parent;
        },
    },

    mounted() {
        this.$nextTick(() => this.register());
    },

    beforeUnmount() {
        this.multipanelDockLayout?.unregisterZoneBlock?.(this._blockKey);
    },

    methods: {
        register() {
            if (!this.multipanelDockLayout?.registerZoneBlock) return;
            const zone = this.resolvedZone;
            if (!zone) {
                console.warn(
                    'MultipanelDockZone: zone required on root zone block',
                );
                return;
            }
            this.multipanelDockLayout.registerZoneBlock(this._blockKey, {
                zone,
                layout: this.layout,
                orientation: this.orientation,
                sizes: this.sizes,
                children: [],
            });
        },
    },
};
</script>
